/*
 * File: SelectTags.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 20, 2025 at 11:54 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: May 7, 2025 at 12:56 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, forwardRef, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';

// Utilities
import { isValidNode, parseMarkdownLinks } from '../../../utilities/parser';
import { formatTagFamilies } from '../../../utilities/tags';

// Components
import { Typography } from '../../Typography';
import { Tag } from '../../Tag';
import { Button } from '../../Button';
import { ToolTip } from '../../ToolTip';
import { LocaleLink } from '../../LocaleLink';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../constants/base.constants';

// Styles
import * as S from './SelectTags.styles';
import { TagSuggestionModal } from '../../TagSuggestionModal';


/**
 * Configuration
 */

// Parser
const ReactParser = new Parser();

// Node Definitions
const processNodeDefinitions = new ProcessNodeDefinitions();

// Parsing Instructions
const ParsingInstructions = [
	{
		shouldProcessNode(node) {
			return node.name === 'a';
		},
		processNode(node, children) {
			return <LocaleLink key={node.attribs.href} to={node.attribs.href} target="_blank">{children}</LocaleLink>;
		}
	},
	{
		shouldProcessNode() { return true; },
		processNode: processNodeDefinitions.processDefaultNode
	}
];


/**
 * Component
 */

export const SelectTags = forwardRef(({
	className, storyTags, moveForward, moveBackward, selectedTags, setSelectedTags, isVisible
}, ref) => {

	// Create state
	const [showTags, setShowTags] = useState(isVisible);
	const [isTagSuggestionModalOpen, setIsTagSuggestionModalOpen] = useState(false);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get partner content
	const partnerContent = useMemo(() => partner?.content?.pages?.share_story?.components?.select_tags, [partner]);

	// Organize tags
	const tagFamilies = useMemo(() => formatTagFamilies(storyTags, t, partner?.id), [storyTags, t, partner]);

	// Handle tag toggle selection
	const toggleTag = (tag) => {
		if (selectedTags.some((tt) => tt.tag_id === tag.tag_id)) {
			setSelectedTags(selectedTags.filter((ttt) => ttt.tag_id !== tag.tag_id));
		} else {
			setSelectedTags([...selectedTags, tag]);
		}
	};

	// Handle next action
	const nextAction = () => {

		// Move to next step
		moveForward();

	};

	// Perform actions on visibility change
	useEffect(() => {
		if (isVisible) {

			// Update show tags
			setShowTags(true);

		} else {

			// Update show tags
			setTimeout(() => {
				setShowTags(false);
			}, 300);

		}
	}, [isVisible]);

	// Perform actions on component load
	useEffect(() => {}, []);

	// Return component
	return (
		<S.StepContainer ref={ref} className={className}>
			<S.InnerStep>

				{/* Content Container */}
				<S.ContentContainer>

					{/* Title */}
					<Typography tag="h1" weight="black">
						{!partnerContent?.title ? (
							<Trans t={t} i18nKey="pageShareStory.selectTags.title">
								Thank you.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.title[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>

					{/* Subtitle */}
					<Typography tag="p">
						{!partnerContent?.subtitle ? (
							<Trans t={t} i18nKey="pageShareStory.selectTags.subtitle">
								Would you like to help categorize your story?
								{' '}
								<strong>This is completely optional.</strong>
								{' '}
								Categories can help survivors with similar experiences find your story.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.subtitle[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>

				</S.ContentContainer>

				{/* Tag Family Container */}
				{showTags && (
					<S.TagFamilyContainer>
						{tagFamilies.map((family) => (
							<S.TagFamily key={family.family}>

								{/* Title */}
								<Typography tag="h5" weight="bold">{family.family_name}</Typography>

								{/* Tags */}
								<S.TagContainer>
									{family.tags.map((tag) => (
										<Tag
											key={tag.tag_id}
											onClick={() => { toggleTag(tag); }}
											isActive={selectedTags.some((tt) => tt.tag_id === tag.tag_id)}
											data-tip={tag.description_locales[locale.localeBaseId]}
											data-for={tag.tag_id}
										>
											{tag.description_locales[locale.localeBaseId] && <ToolTip id={tag.tag_id} />}
											<Typography tag="p" variation="2" weight="medium" center={false}>
												{tag.title_locales[locale.localeBaseId]}
											</Typography>
										</Tag>
									))}
								</S.TagContainer>

							</S.TagFamily>
						))}
					</S.TagFamilyContainer>
				)}

				{/* Suggestion Container */}
				<S.SuggestionContainer>
					<Typography tag="p" variation="3">
						<Trans t={t} i18nKey="pageShareStory.selectTags.suggestionTag">
							We are happy to consider additional tags. If you have a suggestion, please
							{' '}
							<S.SuggestionButton onClick={() => { setIsTagSuggestionModalOpen(true); }}>submit it here</S.SuggestionButton>
							.
						</Trans>
					</Typography>
				</S.SuggestionContainer>

				{/* Continue Button */}
				<S.ActionContainer>
					<Button size="large" variant="text" onClick={moveBackward}>
						<FontAwesomeIcon icon={['fas', 'arrow-left-long']} />
						<Typography>
							<Trans t={t} i18nKey="actions.back">
								Back
							</Trans>
						</Typography>
					</Button>
					<Button size="large" onClick={nextAction}>
						<Typography>
							<Trans t={t} i18nKey="actions.continue">
								Continue
							</Trans>
						</Typography>
						<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
					</Button>
				</S.ActionContainer>

				{/* Legal Snippet */}
				<S.LegalSnippet />

			</S.InnerStep>

			{/* Tag Suggestion Modal */}
			<TagSuggestionModal
				isOpen={isTagSuggestionModalOpen}
				handleClose={() => { setIsTagSuggestionModalOpen(false); }}
			/>

		</S.StepContainer>
	);
});


/**
 * Configuration
 */

SelectTags.displayName = 'SelectTags';
SelectTags.propTypes = {
	className: PropTypes.string,
	storyTags: PropTypes.arrayOf(PropTypes.shape()),
	moveForward: PropTypes.func,
	moveBackward: PropTypes.func,
	selectedTags: PropTypes.arrayOf(PropTypes.shape()),
	setSelectedTags: PropTypes.func,
	isVisible: PropTypes.bool
};
SelectTags.defaultProps = {
	className: null,
	storyTags: [],
	moveForward: null,
	moveBackward: null,
	selectedTags: [],
	setSelectedTags: null,
	isVisible: false
};
